export class CartQf {
  constructor() {
    this.cart = window.QF.cart;
    this.isUpdatingGwp = false;
    this.gwpUpdateTimeout = null;

    this.endpoints = {
      add: {
        url: 'cart/add.js',
        method: 'post'
      },
      get: {
        url: 'cart.js',
        method: 'get'
      },
      update: {
        url: 'cart/update.js',
        method: 'post'
      },
      change: {
        url: 'cart/change.js',
        method: 'post'
      },
      clear: {
        url: 'cart/clear.js',
        method: 'post'
      }
    };

    this.selectors = {
      cartDrawerContainer: '[cart-drawer-container]',
      cartTabs: '[side-cart-tab]',
      cartContent: '[side-cart-form]',
      savedCartTab: '[saved-cart-tab]',
      quickViewContainer: '[quick-view-container]',
      sideCartRecommendations: '[side-cart-recommendations]',
      sideCartRecommendationsSection: '[side-cart-recommendations-section]',
      sideCartRecommendationsContainer: '[side-cart-recommended-container]',
      cartRecommendations: '[cart-recommendations]',
      cartRecommendationsSlider: '[cart-recommendations-slider]',
      cartItemsContainer: '[cart-items-container]',
      cartCount: '[cart-count]',
      openSideCartTrigger: '[open-side-cart]',
      sideCartInner: '[side-cart-inner]',
      cartContainer: '[cart-container]',
      sideCartTotal: '[side-cart-total]',
      mobileRecommended: '[side-cart-inner] [cart-recommendations]',
      mobileRecommendedHeading: '[side-cart-inner] [cart-recommendations] .heading',
    };

    // Keep track of gwp unlocked
    this._lastUnlocked = 0;

    this.toggleBlackFridayPricing();

    window.addEventListener("cart_changed", () => {
      this.toggleBlackFridayPricing();
    });
  }

  dispatchEvent(event_name, includeCart) {
    let contents = null;
    if (includeCart) {
      contents = {
        detail: {
          cart: this.cart,
        },
      };
    }
    window.dispatchEvent(new CustomEvent(event_name, contents));
  }

  async add(data) {
    try {
      const responseData = await this.request('add', data);
      this.mergeWithCurrentCart(responseData);
      this.dispatchEvent("cart_changed", true);
      this.dispatchEvent("item_added", true);
      
      // Small delay to ensure DOM has updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Update GWP UI after adding items (debounced)
      this.debouncedUpdateGwpUi();
      
      return this.cart;
    } catch (error) {
      throw error;
    }
  }

  async get() {
    try {
      const response = await this.request('get');
      this.cart = response;
      return this.cart;
    } catch (error) {
      throw error;
    }
  }

  async update(data) {
    try {
      const responseData = await this.request('update', data);
      this.cart = responseData;
      this.dispatchEvent("cart_changed", true);
      
      // Small delay to ensure DOM has updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Update GWP UI after updating cart (debounced)
      this.debouncedUpdateGwpUi();
      
      return this.cart;
    } catch (error) {
      throw error;
    }
  }

  async change(data) {
    try {
      const responseData = await this.request('change', data);
      this.cart = responseData;
      this.dispatchEvent("cart_changed", true);
      
      // Small delay to ensure DOM has updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Update GWP UI after changing cart (debounced)
      this.debouncedUpdateGwpUi();
      
      return this.cart;
    } catch (error) {
      throw error;
    }
  }

  async remove(variant_id) {
    try {
      const updates = {
        updates: {
          [variant_id]: 0,
        }
      };
      const { data: responseData } = await this.request('update', updates);
      this.cart = responseData;
      this.dispatchEvent("cart_changed", true);
      this.dispatchEvent("item_removed", true);
      return this.cart;
    } catch (error) {
      throw error;
    }
  }

  async clear() {
    try {
      const { data: responseData } = await this.request('clear');
      this.cart = responseData;
      this.dispatchEvent("cart_changed", true);
      this.dispatchEvent("cart_cleared", true);
      return this.cart;
    } catch (error) {
      throw error;
    }
  }

  async request(key, data) {
    try {
      const url = window.Shopify.routes.root + this.endpoints[key].url;
      const method = this.endpoints[key].method;
      const headers = {
        'Content-Type': 'application/json'
      };

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(data)
      });


      if (response.ok) {
        return response.json();
      } else {
        throw await response.json();
      }
    } catch (error) {

      // Generate Toasty Message
      let options = {
        title: error.message,
        message: error.description,
        delay: 5000
      }
      window.toasty.addToast(options);

      throw error;
    }
  }

  async updateNote(note) {
    try {
      const responseData = await this.request('update', { note });
      this.cart = responseData;
      console.log('cart.note is now:', this.cart.note);
      this.dispatchEvent("cart_changed", true);
      return this.cart;
    } catch (err) {
      console.error('Note update failed', err);
      throw err;
    }
  }

  mergeWithCurrentCart(data) {
    if (data.items) {
      data.items.forEach((item) => {
        let index = this.cart.items.findIndex(e => e.id === item.id);
        if (index > -1) {
          this.cart.items[index] = item;
        } else {
          this.cart.items.push(item);
        }
      });
    }
  }

  /**
   * Wait for cart to be ready
   */
  async waitForCartReady(maxAttempts = 10, delay = 100) {
    for (let i = 0; i < maxAttempts; i++) {
      if (this.cart && Array.isArray(this.cart.items)) {
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    console.warn('[GWP] Cart not ready after maximum attempts');
    return false;
  }

  /**
   * Wait for an element to be available in the DOM
   */
  async waitForElement(selector, maxAttempts = 20, delay = 100) {
    for (let i = 0; i < maxAttempts; i++) {
      const element = document.querySelector(selector);
      if (element) {
        return element;
      }
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    console.warn(`[GWP] Element ${selector} not found after maximum attempts`);
    return null;
  }

  /**
   * GWP
   */
  getUnlockedGiftsCount() {
    const thresholds = (window.QF.settings.promotionalProducts?.thresholds || [])
      .map(Number)
      .sort((a, b) => a - b);

    const subtotal = this.cart.items_subtotal_price / 100;
    return thresholds.filter(t => subtotal >= t).length;
  }

  /**
   * Debounced GWP update to prevent multiple calls
   */
  debouncedUpdateGwpUi() {
    // Clear any existing timeout
    if (this.gwpUpdateTimeout) {
      clearTimeout(this.gwpUpdateTimeout);
    }
    
    // Set a new timeout to run the update after a short delay
    this.gwpUpdateTimeout = setTimeout(() => {
      this.updateGwpUi();
    }, 200); // 200ms delay to batch multiple calls
  }

  /**
   * Update UI for GWP
   */
  async updateGwpUi() {
    // Prevent re-entry while already updating
    if (this.isUpdatingGwp) {
      console.log('[GWP] Already updating, skipping...');
      return;
    }
    
    // Wait for cart to be properly initialized
    if (!this.cart || !Array.isArray(this.cart.items)) {
      console.log('[GWP] Cart not ready, waiting...');
      await this.waitForCartReady();
    }
    
    // Refresh cart data to ensure we have the latest information
    try {
      await this.get();
     
      // Validate cart structure
      if (!this.cart || typeof this.cart.item_count === 'undefined') {
        console.error('[GWP] Invalid cart structure after refresh:', this.cart);
        this.isUpdatingGwp = false;
        return;
      }
    } catch (error) {
      console.error('[GWP] Failed to refresh cart:', error);
      this.isUpdatingGwp = false;
      return;
    }
    
    if (this.cart.item_count === 0) {
      console.log('[GWP] Cart is empty, skipping GWP update');
      this.isUpdatingGwp = false;
      return;
    }
    
    this.isUpdatingGwp = true;

    // Wait for the side-cart-total element to be available
    const subtotalEl = await this.waitForElement('[side-cart-total]');
    
    if (!subtotalEl) {
      console.warn('[GWP] Side cart total element not found after waiting');
      this.isUpdatingGwp = false;
      return;
    }

    try {
        // 1. Refresh subtotal
        this.cart.items_subtotal_price = Number(subtotalEl.dataset.subTotal);

        // 2. Compute current vs unlocked
        const unlocked = this.getUnlockedGiftsCount();

        // 3. Promo products array
        const products = window.QF.settings.promotionalProducts?.products || [];
        // console.log(products);

        // 4a. If we’ve just crossed above thresholds, add those gifts
        if (unlocked > this._lastUnlocked) {
          //console.log('[GWP] Threshold rose from', this._lastUnlocked, 'to', unlocked);
          for (let idx = this._lastUnlocked; idx < unlocked; idx++) {
            const p = products[idx];
            if (!p) {
              //console.log(`[GWP] products[${idx}] is undefined—skipping add.`);
              continue;
            }
            const variantId = p.id;
            if (!variantId) {
              //console.log(`[GWP] No variantId for products[${idx}]—skipping add.`);
              continue;
            }

            // Get fresh cart data to ensure we have the most up-to-date information
            try {
              const resp = await fetch(window.Shopify.routes.root + 'cart.js');
              if (!resp.ok) throw new Error('Network response was not ok');
              const freshCart = await resp.json();
              this.cart = freshCart;
            } catch (err) {
              console.error('[GWP] Failed to fetch fresh cart for add check:', err);
            }

            // only add if not already in cart
            const alreadyInCart = this.cart.items.some(i => i.variant_id === variantId);
            //console.log(`[GWP] Checking ADD for variantId ${variantId}: alreadyInCart?`, alreadyInCart);
            if (!alreadyInCart) {
              // console.log(`[GWP] Calling add({id: ${variantId}, quantity:1})`);

              // Await the add(...) call so we don't race.
              try {
                await this.add({ id: variantId, quantity: 1, properties: { "_gwp": "true" } });
                // console.log(`[GWP] Successfully called add for ${variantId}`);
              } catch (err) {
                //console.error(`[GWP] GWP add failed for ${variantId}:`, err);
              }

              // Immediately re-fetch the cart so this.cart.items is up-to-date
              try {
                const resp = await fetch(window.Shopify.routes.root + 'cart.js');
                if (!resp.ok) throw new Error('Network response was not ok');
                const freshCart = await resp.json();
                this.cart = freshCart;
                //console.log('[GWP] After fetch /cart.js, this.cart.items =', JSON.stringify(this.cart.items));
              } catch (err) {
                //console.error('[GWP] raw fetch(/cart.js) after add failed:', err);
              }
            } else {
              //console.log(`[GWP] Variant ${variantId} already in cart—skipping add.`);
            }
          }
        }

        // 4b. If we’ve fallen below thresholds, remove the now‐invalid gifts
        else if (unlocked < this._lastUnlocked) {
          //console.log('[GWP] Threshold decreased from', this._lastUnlocked, 'to', unlocked);
          for (let idx = unlocked; idx < this._lastUnlocked; idx++) {
            const p = products[idx];
            if (!p) {
              //console.log(`[GWP] products[${idx}] is undefined—skipping.`);
              continue;
            }
            const variantId = p.id;
            if (!variantId) {
              //console.log(`[GWP] No variantId for products[${idx}]—skipping.`);
              continue;
            }

            // Get fresh cart data to ensure we have the most up-to-date information
            try {
              const resp = await fetch(window.Shopify.routes.root + 'cart.js');
              if (!resp.ok) throw new Error('Network response was not ok');
              const freshCart = await resp.json();
              this.cart = freshCart;
            } catch (err) {
              console.error('[GWP] Failed to fetch fresh cart for remove check:', err);
            }

            const inCart = this.cart.items.some(i => i.variant_id === variantId);
            //console.log(`[GWP] Checking REMOVE for variantId ${variantId}: inCart?`, inCart);
            if (inCart) {
              // console.log(`[GWP] Calling remove(${variantId})`);
              await this.remove(variantId)
                .then(() => console.log(`[GWP] Successfully removed gift ${variantId}`))
                .catch(err => console.error(`[GWP] GWP remove failed for ${variantId}:`, err));

              try {
                const resp = await fetch(window.Shopify.routes.root + 'cart.js');
                if (!resp.ok) throw new Error('Network response was not ok');
                const freshCart = await resp.json();
                this.cart = freshCart;
                //console.log('[GWP] After fetch /cart.js, this.cart.items =', JSON.stringify(this.cart.items));
              } catch (err) {
                console.error('[GWP] raw fetch(/cart.js) failed:', err);
              }
            } else {
            // console.log(`[GWP] Variant ${variantId} not in cart—nothing to remove.`);
            }
          }
        } else {
          //console.log('[GWP] Threshold unchanged—no add/remove.');
        }

        // 5. Update our tracker
        this._lastUnlocked = unlocked;
    } finally {
      this.isUpdatingGwp = false;
    }
  }

  openSideCart() {
    this.reRenderSideCart(() => {
      const cart_tabs = document.querySelectorAll(this.selectors.cartTabs);
      const cart_content = document.querySelector(this.selectors.cartContent);
      const saved_content = document.querySelector(this.selectors.savedCartTab);
      const quickview = document.querySelector(this.selectors.quickViewContainer);

      if (quickview) {
        quickview.classList.remove('active');
      }

      cart_content.style.display = 'flex';
      saved_content.style.display = 'none';

      cart_tabs[0].classList.add('active');
      cart_tabs[1].classList.remove('active');

      let sideCartContainer = document.querySelector(this.selectors.cartDrawerContainer);
      if (sideCartContainer) {
        sideCartContainer.classList.add("open");
        document.body.classList.add("fixed");
        sideCartContainer.style.height = window.innerHeight + "px";
        window.addEventListener("resize", () => {
          sideCartContainer.style.height = window.innerHeight + "px";
        });
        sideCartContainer.querySelector(this.selectors.sideCartRecommendations).classList.add('open');
        this.dispatchEvent("side_cart_opened", false);
      }

      this.hideMobileRecommendations();
    });
  }

  closeSideCart() {
    let sideCartContainer = document.querySelector(this.selectors.cartDrawerContainer);
    if (sideCartContainer) {
      sideCartContainer.querySelector(this.selectors.sideCartRecommendations).classList.remove('open');
      sideCartContainer.classList.remove("open");
      document.body.classList.remove("fixed");
      this.dispatchEvent("side_cart_closed", false);
    }
  }

  closeSideCartRecommended() {
    let sideCartRecommendations = document.querySelector(this.selectors.sideCartRecommendations);
    if (sideCartRecommendations) {
      sideCartRecommendations.classList.remove('open');
    }
  }

  hideMobileRecommendations() {
    if (window.innerWidth < 1240) {
      // give the recs time to paint
      setTimeout(() => {
        const mobileRecommended = document.querySelector(this.selectors.mobileRecommended);
        if (!mobileRecommended) return;

        const fullHeight   = mobileRecommended.offsetHeight;
        const hideDistance = fullHeight - 45;
        mobileRecommended.style.marginBottom = `-${hideDistance}px`;
      }, 500);
    }
  }

  async reRenderSideCart(successCallback = null) {

    const container = document.querySelector(this.selectors.cartDrawerContainer);
    container?.classList.add('product-recommendations-loading');

    // UPDATE CART COUNT
    const updateCartCount = () => {
      // grab the container properly
      const cartEl = document.querySelector(this.selectors.cartItemsContainer);
      if (!cartEl) {
        console.warn('[CartQf] cart-items-container not found');
        return;
      }

      // now safe to read the attribute
      const cart_count = cartEl.getAttribute('data-items-count');

      // grab your count bubbles and side-cart button
      const cartCountEls = document.querySelectorAll(this.selectors.cartCount);
      const cartBtnEl    = document.querySelector(this.selectors.openSideCartTrigger);

      if (!cartCountEls.length || !cartBtnEl) {
        console.warn('[CartQf] cart-count or open-side-cart trigger not found');
        return;
      }

      cartCountEls.forEach(countEl => {
        if (cart_count === '0') {
          countEl.classList.add('empty-cart');
          cartBtnEl.classList.add('empty-cart');
        } else {
          countEl.classList.remove('empty-cart');
          cartBtnEl.classList.remove('empty-cart');
        }
        countEl.textContent = cart_count;
      });
    };

    try {
      const response = await fetch(window.Shopify.routes.root + "cart?sections=fs-side-cart");
      if (!response.ok) {
        throw new Error("Error: " + response.status);
      }
      const data = await response.json();
      const html = data["fs-side-cart"];

      const side_cart_inner = document.querySelector(this.selectors.sideCartInner);
      if (side_cart_inner && html) {
        side_cart_inner.innerHTML = html;
        updateCartCount();
        this.debouncedUpdateGwpUi();

        let first_cart_item = side_cart_inner.querySelector('.item:not([data-type="promo-product"])');
        if (first_cart_item) {
          first_cart_item = first_cart_item.dataset.productId;
        }

        if (first_cart_item && !this.isUpdatingGwp) {
          const productRecommendationsSection = document.querySelector(this.selectors.cartRecommendations);
          const section_id = productRecommendationsSection.dataset.sectionId;
          const recommendationsUrl = `/recommendations/products?section_id=${section_id}&product_id=${first_cart_item}&limit=5`;

          const recommendationsResponse = await fetch(recommendationsUrl);
          if (recommendationsResponse.ok) {
            const recommendationsText = await recommendationsResponse.text();
            const recommendationsHtml = document.createElement('div');
            recommendationsHtml.innerHTML = recommendationsText;
            const recommendations = recommendationsHtml.querySelector(this.selectors.cartRecommendations);
            if (recommendations && recommendations.innerHTML.trim().length) {
              productRecommendationsSection.innerHTML = recommendations.innerHTML;

              new window.swiper('.side-cart-recommendations-slider', {
                slidesPerView: 1.6,
                spaceBetween: 10,
                slidesOffsetBefore: 20,
                slidesOffsetAfter: 20,
                roundLengths: true,
                breakpoints: {
                  1200: {
                    slidesPerView: 2.4
                  }
                },
                lazy: {
                  enabled: true,
                  checkInView: false,
                  loadOnTransitionStart: true,
                  loadPrevNext: true,
                  loadPrevNextAmount: 1
                },
                scrollbar: {
                  el: '.swiper-scrollbar',
                  draggable: true,
                },
                on: {
                  init: () => {
                    setTimeout(() => {
                      document.querySelector(this.selectors.cartDrawerContainer)?.classList.remove('product-recommendations-loading');
                    }, 500);
                  }
                }
              });

              if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
                window.yotpo.refreshWidgets();
              } else if (window.yotpo && typeof window.yotpo.initWidgets === 'function') {
                // some older/newer Yotpo versions expose initWidgets()
                window.yotpo.initWidgets();
              }
            }
          } else {
            throw new Error("Error: " + recommendationsResponse.status);
          }
        }

        if (window.innerWidth > 1199.98) {
          if (first_cart_item && !this.isUpdatingGwp) {
            document.querySelector(this.selectors.cartRecommendationsSlider).style.display = 'none';

            const productRecommendationsSection = document.querySelector(this.selectors.sideCartRecommendationsSection);
            const sectionURL = productRecommendationsSection.dataset.url;
            const url = sectionURL.replace("product_id=", "product_id=" + first_cart_item);

            const sectionResponse = await fetch(url);
            if (sectionResponse.ok) {
              const sectionText = await sectionResponse.text();
              const sectionHtml = document.querySelector(this.selectors.sideCartRecommendationsContainer);
              sectionHtml.innerHTML = sectionText;

              new window.swiper('.side-cart-recommendations-slider', {
                slidesPerView: 2,
                spaceBetween: 20,
                roundLengths: true,
                navigation: {
                  nextEl: ".swiper-button-next.cart-rec",
                  prevEl: ".swiper-button-prev.cart-rec",
                },
                lazy: {
                  enabled: true,
                  checkInView: false,
                  loadOnTransitionStart: true,
                  loadPrevNext: true,
                  loadPrevNextAmount: 1
                },
                breakpoints: {
                  1134: {
                    slidesPerView: 1.5,
                    spaceBetween: 20,
                  },
                  1340: {
                    slidesPerView: 2.2,
                    spaceBetween: 20,
                  },
                  1800: {
                    slidesPerView: 3,
                    spaceBetween: 20,
                  },
                },
                on: {
                  init: () => {
                    setTimeout(() => {
                      document.querySelector(this.selectors.cartDrawerContainer)?.classList.remove('product-recommendations-loading');
                    }, 500);
                  }
                }
              });

              if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
                window.yotpo.refreshWidgets();
              } else if (window.yotpo && typeof window.yotpo.initWidgets === 'function') {
                // some older/newer Yotpo versions expose initWidgets()
                window.yotpo.initWidgets();
              }
            } else {
              throw new Error("Error: " + sectionResponse.status);
            }
          } else {
            new window.swiper('.cart-recommendations-slider', {
              slidesPerView: 2,
              spaceBetween: 20,
              navigation: {
                nextEl: ".swiper-button-next.cart-rec",
                prevEl: ".swiper-button-prev.cart-rec",
              },
              lazy: {
                enabled: true,
                checkInView: false,
                loadOnTransitionStart: true,
                loadPrevNext: true,
                loadPrevNextAmount: 1
              },
              breakpoints: {
                1134: {
                  slidesPerView: 2,
                  spaceBetween: 20,
                },
                1500: {
                  slidesPerView: 3,
                  spaceBetween: 20,
                },
              }
            });
          }
        }

        document.querySelector(this.selectors.cartDrawerContainer)?.classList.remove('product-recommendations-loading');
        this.cartRenderedEvent(response, html);

        this.hideMobileRecommendations();

        if (successCallback) {
          successCallback();
        }
      }
    } catch (error) {
      console.log("Error:", error);
    }
  }

  async reRenderCart() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart?sections=fs-cart`);
      
      if (!response.ok) throw new Error("Network response was not ok");
      
      const data = await response.json();
      const html = data["fs-cart"];
      
      if (!html) throw new Error("Cart HTML data is missing");
      
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      
      const cartInner = document.querySelector(this.selectors.cartContainer);
      const newCartContent = tempDiv.querySelector(this.selectors.cartContainer);
      
      if (cartInner && newCartContent) {
        cartInner.innerHTML = newCartContent.innerHTML;
        this.cartRenderedEvent(html);
      } else {
        console.warn("Cart container not found in the DOM or fetched HTML.");
      }
    } catch (error) {
      console.error("Error updating cart:", error);
    }
  }

  cartRenderedEvent(response, html) {
    const event = new CustomEvent("cart_rendered", {
      detail: {
        response,
        html,
      },
    });
    window.dispatchEvent(event);
  }

  async toggleBlackFridayPricing() {
    if (!document.body.getAttribute("black-friday-pricing") === "true") return;

    this.get().then(() => {
      const cartTotal = parseInt(this.cart?.total_price) || 0;
      const cartOgTotal = parseInt(this.cart?.original_total_price) || 0;

      const cartDifference = (cartOgTotal - cartTotal) * 1.0;
      const cartDifferencePercentage = (cartDifference / cartOgTotal) * 100;
      const isThirtyOff = cartDifferencePercentage >= 30 ? true : false;
      
      document.body.classList.toggle("twenty-off-enabled", !isThirtyOff);
      document.body.classList.toggle("thirty-off-enabled", isThirtyOff);
    });
  }
}

// Express checkout
const expressCheckoutTrigger = document.getElementById("express-checkout-trigger");
const expressCheckoutSection = document.getElementById("express-checkout-section");

if (expressCheckoutTrigger && expressCheckoutSection) {
  // Show/hide on trigger click
  expressCheckoutTrigger.addEventListener("click", function () {
    const isHidden = expressCheckoutSection.classList.contains("hidden");
    const openText = expressCheckoutTrigger.getAttribute("data-open-express-checkout-options");
    const closeText = expressCheckoutTrigger.getAttribute("data-close-express-checkout-options");

    expressCheckoutTrigger.innerText = isHidden ? closeText : openText;
    expressCheckoutSection.classList.toggle("hidden");
  });

  // Close when clicking outside
  document.addEventListener("click", function (event) {
    const openText = expressCheckoutTrigger.getAttribute("data-open-express-checkout-options");

    if (
      !expressCheckoutSection.contains(event.target) &&
      !expressCheckoutTrigger.contains(event.target)
    ) {
      if (!expressCheckoutSection.classList.contains("hidden")) {
        expressCheckoutSection.classList.add("hidden");
        expressCheckoutTrigger.innerText = openText;
      }
    }
  });
}